/**
 * Analytics utility using PostHog.
 *
 * PostHog is initialized on app mount via initAnalytics().
 *
 * This module provides:
 * 1. initAnalytics() - Initializes the PostHog SDK
 * 2. trackEvent() - Standalone event tracker (for use outside React components)
 * 3. identifyUser() - Identifies the current user for PostHog
 *
 * For React components, prefer the useAnalytics() hook instead.
 */

import posthog from "posthog-js";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://us.i.posthog.com";

const PII_FIELD_NAMES = new Set(
  [
    "fullLegalName",
    "full_legal_name",
    "dateOfBirth",
    "date_of_birth",
    "mailingAddressStreet",
    "mailingAddressUnit",
    "mailingAddressCity",
    "mailingAddressZip",
    "phoneOnFile",
    "phoneNew",
    "driversLicenseNumber",
    "drivers_license_number",
    "emailOnFile",
    "email_on_file",
  ].map((s) => s.toLowerCase()),
);

const PII_AUTOCOMPLETE = new Set([
  "name",
  "given-name",
  "family-name",
  "bday",
  "bday-day",
  "bday-month",
  "bday-year",
  "tel",
  "tel-national",
  "street-address",
  "address-line1",
  "address-line2",
  "postal-code",
  "email",
]);

function isSensitive(element: HTMLElement | undefined): boolean {
  if (!element) return false;
  const input = element as HTMLInputElement;
  if (input.type === "password") return true;
  if (element.getAttribute("data-sensitive") !== null) return true;

  const name = (input.name || "").toLowerCase();
  if (name && PII_FIELD_NAMES.has(name)) return true;

  const id = (element.id || "").toLowerCase();
  if (id && PII_FIELD_NAMES.has(id)) return true;

  const autoComplete = (input.autocomplete || "").toLowerCase();
  if (autoComplete && PII_AUTOCOMPLETE.has(autoComplete)) return true;

  return false;
}

let initialized = false;

/**
 * Initialize PostHog analytics.
 * Call once on app initialization (in App.tsx or main.tsx).
 */
export function initAnalytics(): void {
  if (typeof window === "undefined" || initialized) return;
  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) console.info("[Analytics] VITE_POSTHOG_KEY not set; PostHog disabled");
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    session_recording: {
      maskAllInputs: false,
      maskInputFn: (text: string, element?: HTMLElement) => {
        if (isSensitive(element)) return "*".repeat(Math.min(text.length, 24));
        return text;
      },
      maskTextSelector: "[data-sensitive]",
    },
  });

  initialized = true;
}

/**
 * Track a custom event in PostHog.
 * Works both inside and outside React components.
 */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window !== "undefined" && initialized) {
    posthog.capture(name, params);
  }

  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${name}`, params);
  }
}

/**
 * Identify the current user in PostHog.
 * Call after successful authentication.
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  if (typeof window !== "undefined" && initialized) {
    posthog.identify(userId, properties);
  }
}
